import { api } from "@/app/app.api";
import { RequestMethod } from "@/shared/utils/RequestMethod";
import { camelToSnake } from "caseparser";

function formatDateToDDMMYYYY(dateStr: string) {
  const [year, month, day] = dateStr.split("-");
  return `${day}/${month}/${year}`;
}

export const coursesApi = api
  .enhanceEndpoints({ addTagTypes: ["courses", "course-fee"] })
  .injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
      courses: builder.query({
        query: ({ branchId, search }: any) => ({
          url: `branchs/${branchId}/courses`,
          method: RequestMethod.GET,
          params: {
            search,
          },
        }),
        transformResponse: (response: any) => ({
          data: response.data,
          coachs: response.coachs,
        }),
        providesTags: ["courses"],
      }),
      createCourse: builder.mutation({
        query: ({
          branchId,
          capacity,
          days,
          description,
          duration,
          endDate,
          fee_ids,
          name,
          slot_place_id,
          startDate,
          startTime,
          trainer,
          userId,
        }: any) => ({
          url: `branchs/${branchId}/courses`,
          method: RequestMethod.POST,
          body: {
            branch_id: branchId,
            capacity,
            day: days,
            description,
            duration,
            date_end: formatDateToDDMMYYYY(endDate),
            coach: trainer,
            fee_ids,
            name,
            slot_place_id,
            date_start: formatDateToDDMMYYYY(startDate),
            time_start: startTime,
            user_id: userId,
          },
        }),
        invalidatesTags: ["courses"],
      }),
      updateCourse: builder.mutation({
        query: ({
          branchId,
          id,
          name,
          description,
          branch_id,
          capacity,
          slot_place_id,
          coach,
          day,
          fee_ids,
          date_start,
          time_start,
          date_end,
          duration,
        }: any) => ({
          url: `branchs/${branchId}/courses/${id}`,
          method: RequestMethod.PUT,
          body: camelToSnake({
            name,
            description,
            branch_id,
            capacity,
            slot_place_id,
            coach,
            day,
            fee_ids,
            date_start,
            time_start,
            date_end,
            duration,
          }),
        }),
        invalidatesTags: ["courses"],
      }),
      deleteCourse: builder.mutation({
        query: ({ branchId, id }: any) => ({
          url: `branchs/${branchId}/courses/${id}`,
          method: RequestMethod.DELETE,
        }),
        invalidatesTags: ["courses"],
      }),
      coursesFee: builder.query({
        query: ({ branchId, search }: any) => ({
          url: `branchs/${branchId}/course-fee`,
          method: RequestMethod.GET,
          params: {
            search,
          },
        }),
        transformResponse: (response: any) => response.data,
        providesTags: ["course-fee"],
      }),
      createCourseFee: builder.mutation({
        query: ({
          branchId,
          name,
          description,
          registration_payment,
          amount,
          currency,
          interval,
          trial_period_days,
          people,
          discount,
          repeat_discount,
        }: any) => ({
          url: `branchs/${branchId}/course-fee`,
          method: RequestMethod.POST,
          body: {
            name,
            description,
            registration_payment,
            amount: Number(amount),
            currency,
            interval,
            trial_period_days,
            people,
            discount,
            repeat_discount,
          },
        }),
        invalidatesTags: ["course-fee"],
      }),

      updateCourseFee: builder.mutation({
        query: ({ branchId, id, ...values }: any) => ({
          url: `branchs/${branchId}/course-fee/${id}`,
          method: RequestMethod.PUT,
          body: values,
        }),
      }),
      getCourseUsers: builder.query({
        query: ({ branchId, courseId }) => ({
          url: `branchs/${branchId}/courses/users?id=${courseId}`,
          method: RequestMethod.GET,
        }),
      }),
      deleteCourseUser: builder.mutation({
        query: ({ branchId, courseId, userId }) => ({
          url: `/branchs/${branchId}/course-users/delete`,
          body: {
            course_id: courseId,
            user_id: userId,
          },
          method: RequestMethod.POST,
        }),
      }),
      createPreviusAccessTime: builder.mutation({
        query: ({ branchId, time }: any) => ({
          url: `/branchs/${branchId}/hours`,
          method: RequestMethod.PUT,
          body: {
            type: "course",
            course_time_access: time,
          },
        }),
      }),
    }),
  });

export const {
  useCoursesQuery,
  useCreateCourseMutation,
  useUpdateCourseMutation,
  useDeleteCourseMutation,
  useCoursesFeeQuery,
  useCreateCourseFeeMutation,
  useUpdateCourseFeeMutation,
  useGetCourseUsersQuery,
  useDeleteCourseUserMutation,
  useCreatePreviusAccessTimeMutation,
} = coursesApi;
